import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { TwemojiText } from '../../utils/appleEmoji';
import TwemojiInput from './TwemojiInput';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  MessageCircle, X, ChevronLeft, Send, Smile, Plus, Circle,
  Copy, Reply, CornerUpLeft, Users, Flag,
} from 'lucide-react';
import EmojiPickerButton from './EmojiPickerButton';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../services/cloudApi';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import { formatLastSeen } from '../../utils/formatLastSeen';
import ContextMenu from '../Common/ContextMenu';
import ReportMessageModal from './ReportMessageModal';
import ChatAttachmentPopover from './ChatAttachmentPopover';
import SharedNoteMessage from './SharedNoteMessage';
import CollabInviteMessage from './CollabInviteMessage';
import EventCardMessage from './EventCardMessage';
import PollCardMessage from './PollCardMessage';
import ContactCardMessage from './ContactCardMessage';
import ChatImage from '../Common/ChatImage';

const FloatingChatWidget = () => {
  const { t, i18n } = useTranslation('chat');
  const dateLocale = getDateFnsLocale(i18n.language);
  const location = useLocation();
  const navigate = useNavigate();
  const {
    friends,
    messages,
    selectedFriendId,
    selectedFriend,
    selectedFriendMessages,
    selectFriend,
    sendMessage,
    unreadCount,
    hasUnreadMessages,
    reloadMessages,
    // Group chat
    groups,
    groupMessages,
    selectGroup,
    sendGroupMessage,
    selectedGroupId,
    loadGroupMessages,
  } = useChat();
  const { user, authToken } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [activeChatId, setActiveChatId] = useState(null);
  const [activeIsGroup, setActiveIsGroup] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [friendsTyping, setFriendsTyping] = useState({});

  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Context menu
  const [msgCtxMenu, setMsgCtxMenu] = useState({ isOpen: false, x: 0, y: 0, message: null });
  const [reportModal, setReportModal] = useState({ isOpen: false, messageId: null });

  // Reply state
  const [replyTo, setReplyTo] = useState(null); // { id, content, senderName }

  // Attachment popover
  const [showAttachPopover, setShowAttachPopover] = useState(false);
  const attachBtnRef = useRef(null);

  const messagesEndRef = useRef(null);
  const widgetRef = useRef(null);
  const photoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const twemojiInputRef = useRef(null);

  // Combined list: friends with last message + groups — sorted by last message time
  const combinedList = useMemo(() => {
    const friendItems = friends
      .filter(f => f.lastMessageTime !== null && !f.isDeleted)
      .map(f => ({ ...f, _type: 'friend', _sortTime: new Date(f.lastMessageTime || 0).getTime() }));

    const groupItems = groups
      .filter(g => g.lastMessageTime || g.lastMessage)
      .map(g => ({ ...g, _type: 'group', _sortTime: new Date(g.lastMessageTime || 0).getTime() }));

    return [...friendItems, ...groupItems]
      .sort((a, b) => b._sortTime - a._sortTime);
  }, [friends, groups]);

  // Recent chatters for minimized pill (up to 2 most recent)
  const recentChatters = combinedList.slice(0, 2);

  // Total unread across friends + groups
  const totalUnread = useMemo(() => {
    const friendUnread = friends.reduce((s, f) => s + (f.unreadCount || 0), 0);
    const groupUnread = groups.reduce((s, g) => s + (g.unreadCount || 0), 0);
    return friendUnread + groupUnread;
  }, [friends, groups]);

  // Messages to show in active chat
  const messagesToShow = useMemo(() => {
    if (!activeChatId) return [];
    if (activeIsGroup) {
      return groupMessages[activeChatId] || [];
    }
    let friendId = activeChatId;
    if (!messages[activeChatId]) {
      const friend = friends.find(f => (f.email || f.id) === activeChatId);
      if (friend) friendId = friend.email || friend.id;
    }
    const friendMessages = messages[friendId] || [];
    if (selectedFriendId === friendId && selectedFriendMessages?.length > 0) {
      return selectedFriendMessages;
    }
    return friendMessages;
  }, [activeChatId, activeIsGroup, messages, groupMessages, selectedFriendId, selectedFriendMessages, friends]);

  useEffect(() => {
    if (activeChatId) {
      if (activeIsGroup) {
        if (activeChatId !== selectedGroupId) selectGroup(activeChatId);
        if (!groupMessages[activeChatId] || groupMessages[activeChatId].length === 0) {
          loadGroupMessages(activeChatId);
        }
      } else {
        if (activeChatId !== selectedFriendId) selectFriend(activeChatId);
        if (!messages[activeChatId] || messages[activeChatId].length === 0) {
          reloadMessages(activeChatId);
        }
      }
    }
  }, [activeChatId, activeIsGroup]);

  useEffect(() => {
    const handleTyping = (e) => {
      const { from, isTyping: t } = e.detail;
      setFriendsTyping(prev => ({ ...prev, [from]: t }));
      const fid = selectedFriend?.email || activeChatId;
      if (from === fid) setIsTyping(t);
    };
    window.addEventListener('notesplus-typing', handleTyping);
    return () => { window.removeEventListener('notesplus-typing', handleTyping); setIsTyping(false); };
  }, [activeChatId, selectedFriend]);

  useEffect(() => {
    if (isOpen && activeChatId && messagesEndRef.current && messagesToShow.length > 0) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messagesToShow, isOpen, activeChatId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (widgetRef.current && !widgetRef.current.contains(e.target)) {
        setIsOpen(false);
        setActiveChatId(null);
        setActiveIsGroup(false);
        setMsgCtxMenu({ isOpen: false, x: 0, y: 0, message: null });
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(v => !v);
    if (!isOpen) {
      setActiveChatId(null);
      setActiveIsGroup(false);
      setShowNewChat(false);
      setShowAttachPopover(false);
      setReplyTo(null);
    }
  };

  const handleFriendClick = async (friend) => {
    const friendId = friend.email || friend.id;
    setActiveChatId(friendId);
    setActiveIsGroup(false);
    setShowNewChat(false);
    await selectFriend(friendId);
  };

  const handleGroupClick = async (group) => {
    setActiveChatId(group.id);
    setActiveIsGroup(true);
    setShowNewChat(false);
    selectGroup(group.id);
    if (!groupMessages[group.id] || groupMessages[group.id].length === 0) {
      loadGroupMessages(group.id);
    }
  };

  const handleItemClick = (item) => {
    if (item._type === 'group') {
      handleGroupClick(item);
    } else {
      handleFriendClick(item);
    }
  };

  const handleBackToList = () => {
    setActiveChatId(null);
    setActiveIsGroup(false);
    setReplyTo(null);
  };

  const handleSendMessage = async () => {
    const currentText = twemojiInputRef.current?.getText() ?? messageText;
    if (!currentText.trim() || !activeChatId) return;
    const text = currentText.trim();
    const reply = replyTo;
    setMessageText('');
    twemojiInputRef.current?.clear();
    setReplyTo(null);
    try {
      if (activeIsGroup) {
        await sendGroupMessage(activeChatId, text, 'text', null, null, reply?.id || null);
      } else {
        const friendId = selectedFriendId || activeChatId;
        await sendMessage(friendId, text, 'text', null, null, reply?.id || null);
      }
    } catch {
      setMessageText(text);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageText.trim()) handleSendMessage();
    }
  };

  // Dosya yükleme yardımcısı
  const uploadAndSend = useCallback(async (file, type) => {
    if (!file || !authToken || !activeChatId) return;
    setIsUploadingFile(true);
    try {
      const apiBaseUrl = await getApiBaseUrl();
      const url = `${apiBaseUrl}/chat/upload?type=${type}`;
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      });
      const res = await response.json();
      if (!res?.ok || !res?.fileUrl) {
        throw new Error(res?.message || t('floatingWidget.uploadFailedFallback'));
      }
      const fileUrl = res.fileUrl.startsWith('/') ? res.fileUrl : `/${res.fileUrl}`;
      if (activeIsGroup) {
        await sendGroupMessage(
          activeChatId,
          type === 'image' ? `📷 ${t('floatingWidget.photoLabel')}` : `📎 ${res.fileName || file.name}`,
          type, fileUrl, res.fileName || file.name,
        );
      } else {
        const friendId = selectedFriendId || activeChatId;
        await sendMessage(
          friendId,
          type === 'image' ? `📷 ${t('floatingWidget.photoLabel')}` : `📎 ${res.fileName || file.name}`,
          type, fileUrl, res.fileName || file.name,
        );
      }
    } catch {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'error', title: t('common:errorTitle'), message: t('floatingWidget.fileSendFailedMessage') },
      }));
    } finally {
      setIsUploadingFile(false);
    }
  }, [activeChatId, activeIsGroup, authToken, sendMessage, sendGroupMessage, selectedFriendId]);

  // Context menu handlers
  const handleCopyMsg = () => {
    const msg = msgCtxMenu.message;
    if (msg) {
      const text = msg.content || msg.file_name || '';
      navigator.clipboard?.writeText(text).catch(() => {
        try {
          const ta = document.createElement('textarea');
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        } catch (_) {}
      });
    }
    setMsgCtxMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  const handleReplyMsg = () => {
    const msg = msgCtxMenu.message;
    if (msg) {
      let senderName;
      const isOwn = msg.senderId === 'current_user' || String(msg.senderId || msg.sender_id) === String(user?.id);
      if (isOwn) {
        senderName = user?.name || t('floatingWidget.youShort');
      } else if (activeIsGroup) {
        senderName = msg.senderName || (msg.senderEmail ? msg.senderEmail.split('@')[0] : t('floatingWidget.unknownSender'));
      } else {
        const friend = selectedFriend || friends.find(f => (f.email || f.id) === activeChatId);
        senderName = friend?.nickname || friend?.name || t('floatingWidget.otherPartyFallback');
      }
      setReplyTo({ id: msg.id, content: msg.content || '', senderName });
    }
    setMsgCtxMenu({ isOpen: false, x: 0, y: 0, message: null });
  };

  // Yardımcılar
  const getItemDisplayName = (item) => {
    if (item._type === 'group') return item.name || t('groupInfoSidebar.fallbackGroupName');
    return item.nickname || item.name || item.email || t('floatingWidget.unknownSender');
  };
  const getFriendDisplayName = (f) => f.nickname || f.name || f.email || t('floatingWidget.unknownSender');
  const getFriendAvatar = (f) => f.avatarUrl || f.avatar_url || null;
  const formatMsgTime = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    const diff = Date.now() - date;
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (m < 1) return t('floatingWidget.justNow');
    if (m < 60) return `${m}${t('floatingWidget.minuteShortSuffix')}`;
    if (h < 24) return `${h}${t('floatingWidget.hourShortSuffix')}`;
    if (d < 7) return `${d}${t('floatingWidget.dayShortSuffix')}`;
    return format(date, 'dd MMM', { locale: dateLocale });
  };
  const getLastPreview = (item) => {
    if (item._type === 'group') {
      const msgs = groupMessages[item.id] || [];
      if (msgs.length > 0) {
        const last = msgs[msgs.length - 1];
        const prefix = String(last.senderId) !== String(user?.id)
          ? `${last.senderName || (last.senderEmail ? last.senderEmail.split('@')[0] : '') || ''}: `
          : `${t('groupInfoSidebar.youLabel')}: `;
        if (last.type === 'image') return `${prefix}📷 ${t('floatingWidget.photoLabel')}`;
        if (last.type === 'file') return `${prefix}📎 ${last.file_name || t('forwardModal.typeFallback.file')}`;
        if (last.content) return `${prefix}${last.content}`;
      }
      if (item.lastMessage) return item.lastMessage;
      return t('floatingWidget.noMessagesShort');
    }
    const fid = item.email || item.id;
    const msgs = messages[fid] || [];
    if (msgs.length > 0) {
      const last = msgs[msgs.length - 1];
      if (last.type === 'image') return `📷 ${t('floatingWidget.photoLabel')}`;
      if (last.type === 'file') return `📎 ${last.file_name || t('forwardModal.typeFallback.file')}`;
      if (last.content || last.text) return last.content || last.text;
    }
    if (item.lastMessage) return item.lastMessage;
    return t('floatingWidget.noMessagesShort');
  };

  // Active chat info
  const activeGroup = activeIsGroup ? groups.find(g => g.id === activeChatId) : null;
  const activeFriend = !activeIsGroup
    ? (selectedFriend || friends.find(f => (f.email || f.id) === activeChatId))
    : null;

  if (location.pathname.startsWith('/chat') || !user) return null;

  return (
    <div ref={widgetRef} className="fixed bottom-6 right-8 z-[110]">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.75, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.75, y: 8 }}
            style={{ transformOrigin: 'bottom right', position: 'absolute', bottom: 0, right: 0 }}
            transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
            className="w-[340px] h-[500px] bg-[#0d1424] border border-white/[0.07] rounded-3xl shadow-2xl flex flex-col overflow-hidden"
          >
            {activeChatId === null ? (
              <>
                {/* Sohbet Listesi Header */}
                <div className="flex justify-between items-center px-4 py-3.5 border-b border-white/[0.07] flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-[#e4edf7] font-bold text-base">
                      {showNewChat ? t('floatingWidget.newChatTitle') : t('floatingWidget.chatsTitle')}
                    </h3>
                    {(totalUnread > 0 || hasUnreadMessages) && !showNewChat && (
                      <div className="bg-red-500 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full font-bold">
                        {totalUnread > 99 ? '99+' : totalUnread}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {showNewChat && (
                      <button onClick={() => setShowNewChat(false)} className="p-1 text-[#7a96b0] hover:text-[#c8d8eb] transition-colors">
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                    )}
                    <button onClick={handleToggle} className="p-1 text-[#7a96b0] hover:text-[#c8d8eb] transition-colors">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Sohbet Listesi Body */}
                <div className="flex-1 overflow-y-auto bg-[#0d1424] relative">
                  {showNewChat ? (
                    friends.filter(f => !f.isDeleted && !f.is_deleted).length > 0 ? (
                      friends.filter(f => !f.isDeleted && !f.is_deleted).map((friend) => {
                        const fid = friend.email || friend.id;
                        const avatar = getFriendAvatar(friend);
                        const name = getFriendDisplayName(friend);
                        return (
                          <div
                            key={fid}
                            onClick={() => handleFriendClick(friend)}
                            className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#161f31] transition-colors border-b border-white/[0.04]"
                          >
                            <div className="relative flex-shrink-0">
                              {avatar ? (
                                <img src={avatar} alt={name} className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-[#161f31] flex items-center justify-center">
                                  <span className="text-[#c8d8eb] text-sm font-semibold">{name.charAt(0).toUpperCase()}</span>
                                </div>
                              )}
                              {friend.is_online && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#0d1424] rounded-full" />}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm font-semibold text-[#e4edf7] truncate">{name}</span>
                              <span className="text-xs text-[#7a96b0]">{friend.is_online ? t('floatingWidget.onlineLabel') : t('floatingWidget.offlineLabel')}</span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full p-6">
                        <p className="text-sm text-[#7a96b0]">{t('floatingWidget.noFriendsYet')}</p>
                      </div>
                    )
                  ) : combinedList.length > 0 ? (
                    combinedList.map((item) => {
                      const isGroup = item._type === 'group';
                      const itemId = isGroup ? item.id : (item.email || item.id);
                      const unread = item.unreadCount || 0;
                      const name = getItemDisplayName(item);
                      const lastMsg = getLastPreview(item);
                      const lastTime = formatMsgTime(item.lastMessageTime);
                      return (
                        <div
                          key={`${isGroup ? 'g' : 'f'}_${itemId}`}
                          onClick={() => handleItemClick(item)}
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#161f31] transition-colors border-b border-white/[0.04]"
                        >
                          <div className="relative flex-shrink-0">
                            {isGroup ? (
                              <div className="w-11 h-11 rounded-full bg-[#1e2d47] flex items-center justify-center border border-white/[0.07]">
                                {item.avatar_url || item.avatarUrl ? (
                                  <img src={item.avatar_url || item.avatarUrl} alt={name} className="w-11 h-11 rounded-full object-cover" />
                                ) : (
                                  <Users className="w-5 h-5 text-[#3d8ef0]" />
                                )}
                              </div>
                            ) : (() => {
                              const avatar = getFriendAvatar(item);
                              return avatar ? (
                                <img src={avatar} alt={name} className="w-11 h-11 rounded-full object-cover" />
                              ) : (
                                <div className="w-11 h-11 rounded-full bg-[#161f31] flex items-center justify-center">
                                  <span className="text-[#c8d8eb] text-sm font-semibold">{name.charAt(0).toUpperCase()}</span>
                                </div>
                              );
                            })()}
                            {!isGroup && item.is_online && (
                              <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-[#0d1424] rounded-full" />
                            )}
                          </div>
                          <div className="flex flex-col flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="text-sm font-semibold text-[#e4edf7] truncate">{name}</span>
                              {lastTime && <span className="text-[#7a96b0] text-xs ml-2 flex-shrink-0">{lastTime}</span>}
                            </div>
                            <div className="flex items-center justify-between">
                              {!isGroup && friendsTyping[itemId] ? (
                                <p className="text-emerald-400 italic text-xs">{t('floatingWidget.typingLabel')}</p>
                              ) : (
                                <p className="text-xs text-[#7a96b0] truncate flex-1"><TwemojiText text={String(lastMsg || '')} /></p>
                              )}
                              {unread > 0 && (
                                <div className="w-2 h-2 bg-[#2563eb] rounded-full ml-2 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full p-6">
                      <MessageCircle className="w-10 h-10 text-[#3d8ef1] mb-3" />
                      <p className="text-sm text-[#7a96b0]">{t('floatingWidget.noMessagesYet')}</p>
                    </div>
                  )}

                  {/* + FAB */}
                  <button
                    onClick={() => setShowNewChat(v => !v)}
                    className="absolute bottom-4 right-4 w-10 h-10 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
                    title={showNewChat ? t('floatingWidget.backButton') : t('floatingWidget.newChatButton')}
                  >
                    {showNewChat ? <ChevronLeft className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Aktif Sohbet Header */}
                <div className="flex items-center gap-3 px-3 py-3 border-b border-white/[0.07] flex-shrink-0 bg-[#0d1424]">
                  <button onClick={handleBackToList} className="p-1 text-[#7a96b0] hover:text-[#c8d8eb] transition-colors flex-shrink-0">
                    <ChevronLeft className="w-5 h-5" />
                  </button>

                  {activeIsGroup ? (
                    <div
                      className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer group"
                      onClick={() => { selectGroup(activeChatId); setIsOpen(false); navigate('/chat'); }}
                      title={t('floatingWidget.goToGroupChat')}
                    >
                      <div className="w-9 h-9 rounded-full bg-[#1e2d47] flex items-center justify-center border border-white/[0.07] flex-shrink-0">
                        {activeGroup?.avatar_url || activeGroup?.avatarUrl ? (
                          <img src={activeGroup.avatar_url || activeGroup.avatarUrl} alt={activeGroup.name} className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <Users className="w-4 h-4 text-[#3d8ef0]" />
                        )}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <h4 className="text-[#e4edf7] font-bold text-sm truncate group-hover:text-[#3d8ef0] transition-colors">
                          {activeGroup?.name || t('groupInfoSidebar.fallbackGroupName')}
                        </h4>
                        <span className="text-[#7a96b0] text-xs truncate">
                          {activeGroup?.memberCount ? t('groupInfoSidebar.memberCountLabel', { count: activeGroup.memberCount }) : t('groupInfoSidebar.fallbackGroupName')}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="flex items-center gap-2.5 flex-1 min-w-0 cursor-pointer group"
                      onClick={() => { selectFriend(activeChatId); setIsOpen(false); navigate('/chat'); }}
                      title={t('floatingWidget.goToChat')}
                    >
                      {(() => {
                        const friend = activeFriend;
                        if (!friend && !activeChatId) return null;
                        const name = friend ? getFriendDisplayName(friend) : t('floatingWidget.unknownSender');
                        const avatar = friend ? getFriendAvatar(friend) : null;
                        const online = friend ? (friend.is_online || friend.status === 'online') : false;
                        const lastSeen = friend?.last_seen;
                        return (
                          <>
                            <div className="relative flex-shrink-0">
                              {avatar ? (
                                <img src={avatar} alt={name} className="w-9 h-9 rounded-full object-cover" />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-[#161f31] flex items-center justify-center">
                                  <span className="text-[#c8d8eb] text-sm font-semibold">{name.charAt(0).toUpperCase()}</span>
                                </div>
                              )}
                              {online && <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-[#0d1424] rounded-full" />}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <h4 className="text-[#e4edf7] font-bold text-sm truncate group-hover:text-[#3d8ef0] transition-colors">{name}</h4>
                              <div className="flex items-center gap-1">
                                {isTyping ? (
                                  <span className="text-emerald-400 italic text-xs">{t('floatingWidget.typingLabel')}</span>
                                ) : (
                                  <>
                                    <Circle className={`w-1.5 h-1.5 flex-shrink-0 ${online ? 'text-emerald-400' : 'text-[#7a96b0]'}`} fill="currentColor" />
                                    <span className="text-[#7a96b0] text-xs truncate">
                                      {online ? t('floatingWidget.onlineLabel') : formatLastSeen(lastSeen) || t('floatingWidget.offlineLabel')}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}

                  <button onClick={handleToggle} className="p-1 text-[#7a96b0] hover:text-[#c8d8eb] transition-colors flex-shrink-0">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Mesajlar */}
                <div className="flex-1 overflow-y-auto px-3 py-3 bg-[#0d1424] space-y-1.5">
                  {messagesToShow.length > 0 ? (
                    [...messagesToShow]
                      .sort((a, b) =>
                        new Date(a.timestamp || a.created_at || 0) -
                        new Date(b.timestamp || b.created_at || 0)
                      )
                      .map((msg) => {
                        const isOwn =
                          msg.senderId === 'current_user' ||
                          String(msg.senderId || msg.sender_id) === String(user?.id);
                        const text = msg.content || msg.text || '';
                        const key = msg.id || `tmp_${msg.timestamp}_${text.slice(0, 8)}`;
                        const isCardType = ['note_share', 'note_collab_invite', 'event_share', 'event_cancelled', 'poll', 'contact'].includes(msg.type);
                        const timeStr = (msg.timestamp || msg.created_at)
                          ? format(new Date(msg.timestamp || msg.created_at), 'HH:mm')
                          : '';
                        // Sender name for group messages
                        const senderLabel = activeIsGroup && !isOwn
                          ? (msg.senderName || (msg.senderEmail ? msg.senderEmail.split('@')[0] : null))
                          : null;

                        return (
                          <div key={key} className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                            {/* Group sender name */}
                            {senderLabel && (
                              <span className="text-[10px] text-[#3d8ef0] font-semibold px-1 mb-0.5 ml-1">{senderLabel}</span>
                            )}
                            {isCardType ? (
                              <div
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setMsgCtxMenu({ isOpen: true, x: e.clientX, y: e.clientY, message: msg });
                                }}
                                className="max-w-[95%] cursor-default"
                              >
                                {msg.replyTo && (
                                  <div className="mb-1.5 px-2 py-1 rounded-lg border-l-2 bg-[#161f31] border-[#3d8ef0] text-[#7a96b0] text-xs">
                                    <p className="truncate">{typeof msg.replyTo === 'object' ? msg.replyTo.content || msg.replyTo.text : ''}</p>
                                  </div>
                                )}
                                {msg.type === 'note_share' && (
                                  <SharedNoteMessage message={msg} isOwnMessage={isOwn} groupId={activeIsGroup ? activeChatId : null} />
                                )}
                                {msg.type === 'note_collab_invite' && (
                                  <CollabInviteMessage message={msg} isOwnMessage={isOwn} groupId={activeIsGroup ? activeChatId : null} />
                                )}
                                {(msg.type === 'event_share' || msg.type === 'event_cancelled') && (
                                  <EventCardMessage message={msg} isOwnMessage={isOwn} groupId={activeIsGroup ? activeChatId : null} />
                                )}
                                {msg.type === 'poll' && (
                                  <PollCardMessage message={msg} isOwnMessage={isOwn} groupId={activeIsGroup ? activeChatId : null} />
                                )}
                                {msg.type === 'contact' && (
                                  <ContactCardMessage message={msg} isOwnMessage={isOwn} />
                                )}
                                {timeStr && (
                                  <p className="text-[10px] mt-1 text-[#7a96b0] px-1">{timeStr}</p>
                                )}
                              </div>
                            ) : (
                              <div
                                onContextMenu={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setMsgCtxMenu({ isOpen: true, x: e.clientX, y: e.clientY, message: msg });
                                }}
                                onDoubleClick={() => {
                                  if (!msg.is_deleted_for_everyone) {
                                    const senderName = isOwn
                                      ? (user?.name || t('floatingWidget.youShort'))
                                      : activeIsGroup
                                        ? (msg.senderName || (msg.senderEmail ? msg.senderEmail.split('@')[0] : t('floatingWidget.unknownSender')))
                                        : (activeFriend?.nickname || activeFriend?.name || t('floatingWidget.otherPartyFallback'));
                                    setReplyTo({ id: msg.id, content: msg.content || '', senderName });
                                  }
                                }}
                                className={`px-3 py-2 rounded-2xl text-sm max-w-[82%] cursor-default select-text ${
                                  isOwn
                                    ? 'bg-[#2563eb] text-white rounded-tr-sm'
                                    : 'bg-[#161f31] text-[#e4edf7] rounded-tl-sm border border-white/[0.06]'
                                }`}
                              >
                                {/* Reply preview */}
                                {msg.replyTo && (
                                  <div className={`text-xs mb-1.5 px-2 py-1 rounded-lg border-l-2 ${
                                    isOwn ? 'bg-white/10 border-white/40 text-white/70' : 'bg-[#0d1424] border-[#3d8ef0] text-[#7a96b0]'
                                  }`}>
                                    {msg.replyTo.senderName && (
                                      <p className="font-semibold mb-0.5 truncate">{msg.replyTo.senderName}</p>
                                    )}
                                    <p className="truncate">{typeof msg.replyTo === 'object' ? (msg.replyTo.content || msg.replyTo.text) : ''}</p>
                                  </div>
                                )}
                                {(msg.type === 'text' || !msg.type) && text && (
                                  <p className="whitespace-pre-wrap break-words"><TwemojiText text={text} /></p>
                                )}
                                {msg.type === 'image' && (msg.file_url || msg.fileUrl) && (
                                  <ChatImage
                                    fileUrl={msg.fileUrl || msg.file_url}
                                    className="max-w-full rounded-lg"
                                  />
                                )}
                                {msg.type === 'image' && !msg.file_url && text && (
                                  <p className="whitespace-pre-wrap break-words">{text}</p>
                                )}
                                {msg.type === 'file' && (
                                  <a
                                    href={msg.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`flex items-center gap-1.5 text-xs underline ${isOwn ? 'text-white/90' : 'text-[#c8d8eb]'}`}
                                  >
                                    📎 {msg.file_name || t('forwardModal.typeFallback.file')}
                                  </a>
                                )}
                                {msg.is_deleted_for_everyone && (
                                  <p className="text-xs italic text-white/40">{t('floatingWidget.messageDeleted')}</p>
                                )}
                                {timeStr && (
                                  <p className={`text-[10px] mt-1 text-right ${isOwn ? 'text-white/50' : 'text-[#7a96b0]'}`}>
                                    {timeStr}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                      <MessageCircle className="w-10 h-10 text-[#3d8ef1] mb-3" />
                      <p className="text-sm text-[#7a96b0]">{t('floatingWidget.noMessagesYet')}</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Alanı */}
                <div className="flex-shrink-0 bg-[#0d1424] border-t border-white/[0.07]">
                  {/* Reply bar */}
                  {replyTo && (
                    <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                      <CornerUpLeft className="w-4 h-4 text-[#3d8ef0] flex-shrink-0" />
                      <div className="flex-1 min-w-0 bg-[#161f31] border-l-2 border-[#3d8ef0] rounded px-2 py-1">
                        <p className="text-[10px] font-semibold text-[#3d8ef0] mb-0.5">{replyTo.senderName}</p>
                        <p className="text-xs text-[#7a96b0] truncate">{replyTo.content}</p>
                      </div>
                      <button onClick={() => setReplyTo(null)} className="text-[#7a96b0] hover:text-[#c8d8eb] flex-shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  <div className="relative flex items-center gap-2 px-3 py-2.5">
                    {/* Gizli dosya input'ları */}
                    <input ref={photoInputRef} type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) await uploadAndSend(f, 'image');
                      }}
                    />
                    <input ref={fileInputRef} type="file" className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) await uploadAndSend(f, 'file');
                      }}
                    />
                    <button
                      ref={attachBtnRef}
                      data-chat-attach-trigger="true"
                      type="button"
                      disabled={isUploadingFile}
                      onClick={() => setShowAttachPopover(v => !v)}
                      title={t('floatingWidget.attachmentsTitle')}
                      className={`flex-shrink-0 transition-colors ${
                        showAttachPopover ? 'text-[#3d8ef0]' : 'text-[#7a96b0] hover:text-[#c8d8eb]'
                      }`}
                    >
                      {isUploadingFile ? (
                        <div className="w-5 h-5 border-2 border-[#3d8ef0] border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                        </svg>
                      )}
                    </button>

                    {/* Input */}
                    <div className="relative flex-1 flex items-center">
                      <TwemojiInput
                        ref={twemojiInputRef}
                        onTextChange={setMessageText}
                        onEnterPress={handleSendMessage}
                        placeholder={t('messageInputPlaceholder')}
                      />
                      {messageText.trim() === '' ? (
                        <EmojiPickerButton
                          onEmojiSelect={(emoji) => twemojiInputRef.current?.insertEmoji(emoji)}
                          position="top-end"
                          className="absolute right-2 text-[#7a96b0] hover:text-[#c8d8eb]"
                        >
                          <Smile className="w-5 h-5" />
                        </EmojiPickerButton>
                      ) : (
                        <button
                          onClick={handleSendMessage}
                          className="absolute right-1.5 w-7 h-7 bg-[#2563eb] hover:bg-[#3d8ef0] rounded-full flex items-center justify-center text-white transition-colors"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Minimized pill */}
      <AnimatePresence>
      {!isOpen && (
        <motion.button
          initial={{ opacity: 0, scale: 0.75 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.75 }}
          style={{ transformOrigin: 'bottom right' }}
          transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleToggle}
          className="bg-[#0d1424] border border-white/[0.07] shadow-lg rounded-full pl-4 pr-2 py-2 flex items-center gap-3 cursor-pointer hover:bg-[#161f31] transition-all relative"
        >
          <MessageCircle className="w-5 h-5 text-[#7a96b0]" />
          <span className="text-[#e4edf7] font-semibold text-sm">{t('floatingWidget.messagesLabel')}</span>
          {recentChatters.length > 0 && (
            <div className="flex -space-x-2">
              {recentChatters.map((item) => {
                const isGroup = item._type === 'group';
                const key = isGroup ? `g_${item.id}` : (item.email || item.id);
                const name = getItemDisplayName(item);
                return (
                  <div key={key} className="relative">
                    {isGroup ? (
                      <div className="w-6 h-6 rounded-full bg-[#1e2d47] border-2 border-[#0d1424] flex items-center justify-center">
                        <Users className="w-3 h-3 text-[#3d8ef0]" />
                      </div>
                    ) : (() => {
                      const avatar = getFriendAvatar(item);
                      return avatar ? (
                        <img src={avatar} alt={name} className="w-6 h-6 rounded-full border-2 border-[#0d1424] object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-[#161f31] border-2 border-[#0d1424] flex items-center justify-center">
                          <span className="text-[10px] text-[#c8d8eb] font-semibold">{name.charAt(0).toUpperCase()}</span>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          )}
          {(totalUnread > 0 || hasUnreadMessages) && (
            <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full font-bold">
              {totalUnread > 99 ? '99+' : totalUnread}
            </div>
          )}
        </motion.button>
      )}
      </AnimatePresence>

      {/* Mesaj Context Menu */}
      <ContextMenu
        isOpen={msgCtxMenu.isOpen}
        position={{ x: msgCtxMenu.x, y: msgCtxMenu.y }}
        items={[
          {
            label: t('floatingWidget.replyAction'),
            icon: Reply,
            onClick: handleReplyMsg,
          },
          {
            label: t('floatingWidget.copyAction'),
            icon: Copy,
            onClick: handleCopyMsg,
          },
          ...(msgCtxMenu.message &&
            msgCtxMenu.message.senderId !== 'current_user' &&
            String(msgCtxMenu.message.senderId || msgCtxMenu.message.sender_id) !== String(user?.id)
            ? [{
                label: t('floatingWidget.reportAction'),
                icon: Flag,
                onClick: () => {
                  setReportModal({ isOpen: true, messageId: msgCtxMenu.message?.id || null });
                },
              }]
            : []),
        ]}
        onClose={() => setMsgCtxMenu({ isOpen: false, x: 0, y: 0, message: null })}
      />

      <ReportMessageModal
        isOpen={reportModal.isOpen}
        messageId={reportModal.messageId}
        onClose={() => setReportModal({ isOpen: false, messageId: null })}
      />

      {/* Attachment Popover */}
      <ChatAttachmentPopover
        isOpen={showAttachPopover}
        onClose={() => setShowAttachPopover(false)}
        anchorRef={attachBtnRef}
        onSharePhoto={() => { setShowAttachPopover(false); photoInputRef.current?.click(); }}
        onShareFile={() => { setShowAttachPopover(false); fileInputRef.current?.click(); }}
        onShareNote={() => {
          setShowAttachPopover(false);
          window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
            detail: { type: 'info', title: t('floatingWidget.shareNoteInfoTitle'), message: t('floatingWidget.shareNoteInfoMessage') },
          }));
        }}
        onCreateEvent={() => { setShowAttachPopover(false); }}
        onCreatePoll={() => { setShowAttachPopover(false); }}
        onShareContact={() => { setShowAttachPopover(false); }}
      />
    </div>
  );
};

export default FloatingChatWidget;
